/**
 * ForgotPasswordForm - Форма відновлення пароля
 *
 * Крок 1: введення email → отримання OTP-коду на пошту
 * Крок 2: введення OTP-коду та нового пароля → зміна пароля
 */

import { Button } from 'antd'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlineLock, AiOutlineUser } from 'react-icons/ai'
import { Link } from 'react-router'

import type { IAppConfig } from '@/Core/Contracts/Configs/IAppConfig'
import { userManager } from '@/Core/Managers'
import { configManager } from '@/Core/Managers/Config'
import { Form, FormField } from '@/Core/UI/Form'
import type { ForgotPasswordFormProps } from '../Auth.types'

import styles from './ForgotPasswordForm.module.css'

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ authPageUrl, onSuccess, onError }) => {
    const { t } = useTranslation()
    const [isLoading, setIsLoading] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [error, setError] = useState<string | null>(null)
    /** Токен відновлення, отриманий на кроці 1 */
    const [recoveryToken, setRecoveryToken] = useState<string | null>(null)

    const appConfig = configManager.get<IAppConfig>('AppConfig')
    const defaultAuthPageUrl = appConfig?.authPageUrl ?? '/auth/login'
    const finalAuthPageUrl = authPageUrl ?? defaultAuthPageUrl

    /** Крок 1: відправка email */
    const handleEmailSubmit = async ({ values, formContext }: { values: Record<string, any>; formContext: any }) => {
        setIsLoading(true)
        setError(null)
        if (formContext?.resetErrors) formContext.resetErrors()

        try {
            const result = await userManager.forgotPassword({ login: values.email })

            if (result.success && result.token) {
                setRecoveryToken(result.token)
            } else {
                const msg = result.error || t('User with such E-Mail not found.', { ns: 'Core' })
                setError(msg)
                if (formContext?.setError) formContext.setError('email', msg)
                if (onError) onError(msg)
            }
        } catch {
            const msg = t('An unknown error has occurred. Please try again later.', { ns: 'Core' })
            setError(msg)
            if (formContext?.setError) formContext.setError('email', msg)
            if (onError) onError(msg)
        } finally {
            setIsLoading(false)
        }
    }

    /** Крок 2: підтвердження OTP і встановлення нового пароля */
    const handleResetSubmit = async ({ values, formContext }: { values: Record<string, any>; formContext: any }) => {
        if (!recoveryToken) return

        if (values.plainPassword !== values.confirmPassword) {
            const msg = t('Passwords do not match.', { ns: 'Core' })
            setError(msg)
            if (formContext?.setError) formContext.setError('confirmPassword', msg)
            return
        }

        setIsLoading(true)
        setError(null)
        if (formContext?.resetErrors) formContext.resetErrors()

        try {
            const result = await userManager.resetPassword({
                token: recoveryToken,
                code: Number(values.code),
                plainPassword: values.plainPassword,
            })

            if (result.success) {
                setIsSuccess(true)
                if (onSuccess) onSuccess()
            } else {
                const msg = result.error || t('An unknown error has occurred. Please try again later.', { ns: 'Core' })
                setError(msg)
                if (formContext?.setError) formContext.setError('code', msg)
                if (onError) onError(msg)
            }
        } catch {
            const msg = t('An unknown error has occurred. Please try again later.', { ns: 'Core' })
            setError(msg)
            if (onError) onError(msg)
        } finally {
            setIsLoading(false)
        }
    }

    const emailFields = {
        email: {
            isRequired: true,
            label: t('E-mail', { ns: 'Core' }),
        },
    }

    const resetFields = {
        code: {
            isRequired: true,
            label: t('Confirmation code', { ns: 'Core' }),
        },
        plainPassword: {
            isRequired: true,
            label: t('New password', { ns: 'Core' }),
        },
        confirmPassword: {
            isRequired: true,
            label: t('Confirm password', { ns: 'Core' }),
        },
    }

    return (
        <div className={styles.forgotPasswordForm}>
            {/* Крок 1: введення email */}
            {!recoveryToken && !isSuccess && (
                <Form fields={emailFields} onSubmit={handleEmailSubmit}>
                    <FormField name="email" inputProps={{ prefix: <AiOutlineUser /> }} />

                    {error && <div className={styles.errorMessage}>{error}</div>}

                    <Link to={finalAuthPageUrl}>
                        <Button className={styles.loginButton} loading={isLoading} type="link" onClick={() => setIsLoading(true)}>
                            {t('Log in', { ns: 'Core' })}
                        </Button>
                    </Link>

                    <Button className={styles.submitButton} type="primary" htmlType="submit" loading={isLoading}>
                        {t('Reset password', { ns: 'Core' })}
                    </Button>
                </Form>
            )}

            {/* Крок 2: введення OTP-коду та нового пароля */}
            {recoveryToken && !isSuccess && (
                <Form fields={resetFields} onSubmit={handleResetSubmit}>
                    <FormField name="code" inputProps={{ prefix: <AiOutlineLock />, maxLength: 6 }} />
                    <FormField name="plainPassword" inputProps={{ prefix: <AiOutlineLock />, type: 'password' }} />
                    <FormField name="confirmPassword" inputProps={{ prefix: <AiOutlineLock />, type: 'password' }} />

                    {error && <div className={styles.errorMessage}>{error}</div>}

                    <Button type="link" onClick={() => { setRecoveryToken(null); setError(null) }}>
                        {t('Back', { ns: 'Core' })}
                    </Button>

                    <Button className={styles.submitButton} type="primary" htmlType="submit" loading={isLoading}>
                        {t('Set new password', { ns: 'Core' })}
                    </Button>
                </Form>
            )}

            {/* Успіх */}
            {isSuccess && (
                <div className={styles.successMessage}>
                    <div>{t('Password has been successfully changed. You can now log in.', { ns: 'Core' })}</div>
                    <Link to={finalAuthPageUrl}>
                        <Button type="primary" style={{ marginTop: 12 }}>
                            {t('Log in', { ns: 'Core' })}
                        </Button>
                    </Link>
                </div>
            )}
        </div>
    )
}

ForgotPasswordForm.displayName = 'ForgotPasswordForm'

